import React, { useEffect, useRef, useState } from 'react'
import { Pipeline, SimplePipeline } from '../../../interfaces/Pipeline'
import { useIntl } from 'react-intl';
import { IconButton, Menu, MenuItem } from "@mui/material";
import useSWR from 'swr';
import useAccessToken from '../../../hooks/AccessToken';
import axios from 'axios';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { insertAttachment, insertMultiAttachment } from '../../../api/ResourceAPI';
import { createPaymentTermin, ICreatePaymentTermin } from '../../../api/PipelineCRUD';
import Swal from 'sweetalert2';
import moment from 'moment-timezone';
import { currencyFormatter, currencyFormatterPreventPropagation, customNumberFormatString, customThousandSeparatorDot, fixFloatingPoint, getCurrencySymbol, isBeforeToday, parseFormattedCurrency, isAfterToday, } from '../../../functions/general';
import {
  CellContext,
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useSettingsStore } from '../../../stores/SettingsStore';
import { CustomField } from '../../../interfaces/Settings';
import { useFormik } from 'formik';
import { PaymentTermin } from '../../../interfaces/Termin/Termin';
import useModalEffect from '../../../hooks/useModalEffect';
import useUser from '../../../hooks/User';
import { v4 as uuidv4 } from 'uuid';
import useHandleCloseModalTermin from '../../../hooks/useHandleCloseModalTermin';
import { usePipelineStore } from '../../../stores/PipelineStore';
import { useMerchantStore } from '../../../stores/MerchantStore';
import { useMeetingStore } from '../../../stores/MeetingsStore';
import { SimpleMerchant } from '../../../interfaces/Merchant';
import { SimpleMeeting } from '../../../interfaces/Meeting';
import { ExpandableElement } from '../../General/ExpandableElement';
import ModalContentTermin from './MainContentTermin/ModalContentTermin';
import CardContentTermin from './MainContentTermin/CardContentTermin';

const API_URL = process.env.REACT_APP_API_URL

interface DetailTerminPipelineProps {
  pipeline: Pipeline
  isModal?: boolean
}

const DetailTerminPipeline = ({ pipeline, isModal = false }: DetailTerminPipelineProps) => {
  const { settings } = useSettingsStore()
  const token = useAccessToken()
  const intl = useIntl()
  const user = useUser()
  const { selectMultiPipeline } = usePipelineStore()
  const modalRef = useRef<HTMLDivElement | null>(null);
  const closeModalRef = useRef<HTMLButtonElement>(null);
  const checkingValuePaymentTerm = (): number => {
    if (typeof pipeline.nominal === 'number') {
      if (selectMultiPipeline.currencyIsRounded) {
        return Math.round(pipeline.nominal)
      }
      return pipeline.nominal
    }
    return Number(parseFormattedCurrency(pipeline.nominal))
  }
  const [unpaymentNominal, setUnpaymentNominal] = useState<number>(checkingValuePaymentTerm())
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [customFieldTermin, setCustomFieldTermin] = useState<CustomField>({})
  const columnHelper = createColumnHelper<PaymentTermin>()
  const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false)
  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false)
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [switchPaidSchedule, setSwitchPaidSchedule] = useState<boolean>(false)
  const [selectedTermin, setSelectedTermin] = useState<PaymentTermin & { index: number } | null>(null);
  const [showAttachmentModal, setShowAttachmentModal] = useState(false)
  const [terminKey, setTerminKey] = useState<string>('')
  const [terminUrl, setTerminUrl] = useState<string>('')
  const [terminFiles, setTerminFiles] = useState<File | File[] | null>(null)
  const inputRef = useRef<(HTMLInputElement | null)[]>([])
  const [selectedTerminNumber, setSelectedTerminNumber] = useState<number>(0)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [mergeData, setMergeData] = useState<PaymentTermin[]>([])
  const [tempPaymentTermin, setTempPaymentTermin] = useState<PaymentTermin[]>([])
  const [isTypeFile, setIsTypeFile] = useState<boolean>(false)
  const [terminErrors, setTerminErrors] = useState<any>({})
  const { simplePipelines } = usePipelineStore()
  const { simpleMerchants } = useMerchantStore()
  const { simpleMeetings } = useMeetingStore()

  useEffect(() => {
    if (settings.termins_custom_fields) {
      setCustomFieldTermin(JSON.parse(settings.termins_custom_fields))
    }
  }, [settings])

  const handleClose = () => {
    setAnchorEl(null);
    setOpenMenuId(null);
  };

  const handleCheckIsTypeAttachment = (url: string | string[] | File | File[], key?: string) => {
    const isFileArray = (value: any[]): value is File[] => {
      return value.every((item): item is File => item instanceof File);
    };

    const isFile = url instanceof File || (Array.isArray(url) && isFileArray(url));

    setIsTypeFile(isFile);

    if (isFile) {
      setTerminFiles(url as File | File[]);
      setShowAttachmentModal(true)
    } else {
      setTerminKey(key || '');
      setTerminUrl(url as any);
      setShowAttachmentModal(true)
    }
  };

  const handleClosePreview = () => {
    setShowAttachmentModal(false);
    setTerminFiles([]);
  }

  const fetcher = async (url: string) => {
    try {
      const response = await axios.get(url, {
        headers: {
          'x-auth-token': token
        }
      })
      return response.data.data
    } catch (error) {
      console.log(error)
    }
  }

  const { data = [], isLoading, mutate: mutateTermin, error: errorTermin } = useSWR<PaymentTermin[]>(`${API_URL}payment-termins?leadId=${pipeline.id}`, fetcher);

  useEffect(() => {
    if (pipeline.nominal && unpaymentNominal !== 0) {
      const isCompanyCurrency = selectMultiPipeline.currencyCode
      const isCompanyCurrencyNeedRound = selectMultiPipeline.currencyIsRounded
      const totalPaid = data.reduce((acc, item) => acc + (item.nominalBeforeVat || 0), 0)
      const remainingAmount = pipeline.nominal - totalPaid
      const finalAmount = isCompanyCurrency === 'IDR' && isCompanyCurrencyNeedRound ? Math.round(remainingAmount) : fixFloatingPoint(remainingAmount, selectMultiPipeline)
      setUnpaymentNominal(finalAmount)
    }
  }, [pipeline.nominal, data])

  const columnCustomField = Object.keys(customFieldTermin).map(key => {
    const fieldType = customFieldTermin[key].type

    if (fieldType === 'text' || fieldType === 'option' || fieldType === 'multiple' || fieldType === 'multipleOption') {
      return columnHelper.accessor(row => row.customFields?.[key], {
        id: key,
        header: () => customFieldTermin[key].name,
        size: 25,
        cell: (info) => {
          const row = info.row.original;
          const values = row.customFields && row.customFields[key] && row.customFields[key].toString();
          return values ? values.split(',').join(', ') : '-';
        },
      })
    } else if (fieldType === 'paragraph') {
      return columnHelper.accessor(row => row.customFields?.[key], {
        id: key,
        header: () => customFieldTermin[key].name,
        size: 30,
        cell: (info) => {
          const row = info.row.original;
          const values = row.customFields && row.customFields[key];
          return values ? (
            <ExpandableElement
              content={
                <div style={{ whiteSpace: 'pre-wrap' }}>
                  {values}
                </div>
              }
              maxHeight={100}
            />
          ) : "-"
        },
      })
    } else if (fieldType === 'date') {
      return columnHelper.accessor(row => row.customFields?.[key], {
        id: key,
        header: () => customFieldTermin[key].name,
        size: 30,
        cell: (info) => {
          const value = info.getValue()
          return value ? moment(value).format("DD MMM YYYY") : '-'
        },
      })
    } else if (fieldType === 'dateTimeRange') {
      return columnHelper.accessor(row => row.customFields?.[key], {
        id: key,
        header: () => customFieldTermin[key].name,
        size: 30,
        cell: (info) => {
          const value = info.getValue()
          const convertValues = value?.map((item: any) => moment(item).format("DD MMM YYYY HH:mm"))
          return convertValues ? convertValues.join(' - ') : '-'
        },
      })
    } else if (fieldType === 'image' || fieldType === 'images') {
      return columnHelper.accessor(row => row.customFields?.[key], {
        id: key,
        header: () => customFieldTermin[key].name,
        size: 25,
        cell: (info) => {
          const row = info.row.original;
          const imageUrl = row.customFields && row.customFields[key];
          return imageUrl ? (
            <span role="button" onClick={() => {
              handleCheckIsTypeAttachment(imageUrl, key)
            }}>
              <i className="fas fs-2 fa-eye"></i>
            </span>
          ) : '-';
        },
      })
    } else if (fieldType === 'file' || fieldType === 'files' || fieldType === 'video' || fieldType === 'videos') {
      return columnHelper.accessor(row => row.customFields?.[key], {
        id: key,
        header: () => customFieldTermin[key].name,
        size: 25,
        cell: (info) => {
          const row = info.row.original;
          const fileUrl = row.customFields && row.customFields[key];
          if (typeof fileUrl === "string") {
            return fileUrl ? (
              <a
                href={fileUrl}
                target="_blank"
                rel="noreferrer"
                className="ms-2 btn btn-sm btn-icon btn-bg-light btn-active-color-primary"
              >
                <i className="fas fa-file-invoice fs-3"></i>
              </a>
            ) : '-';
          } else {
            return fileUrl ? (
              <span role="button" onClick={() => {
                handleCheckIsTypeAttachment(fileUrl, key)
              }}>
                <i className="fas fa-file-invoice fs-3"></i>
              </span>
            ) : '-';
          }
        },
      })
    } else if (fieldType === 'serial') {
      return columnHelper.accessor(row => row.customFields?.[key], {
        id: key,
        header: () => customFieldTermin[key].name,
        size: 25,
        cell: (info) => {
          const row = info.row.original;
          const values = row.customFields && row.customFields[key];
          return typeof values === 'string' ? values : (values as string[])?.length > 0 ? (values as string[]).join('') : '-';
        },
      })
    } else if (fieldType === 'link') {
      return columnHelper.accessor(row => row.customFields?.[key], {
        id: key,
        header: () => customFieldTermin[key].name,
        size: 25,
        cell: (info) => {
          const row = info.row.original;
          const values = row.customFields && row.customFields[key];
          return values ? (
            <a href={values} target="_blank" rel="noreferrer"
              className="btn btn-sm btn-outline btn-outline-default btn-active-light text-gray-600">
              <i className="fas fa-external-link-alt fs-6 ms-2 p-0 me-2"></i>
              {intl.formatMessage({ id: 'FORM.LABEL.LOOK' })}
            </a>
          ) : '-';
        },
      })
    } else if (fieldType === 'phone') {
      return columnHelper.accessor(row => row.customFields?.[key], {
        id: key,
        header: () => customFieldTermin[key].name,
        size: 30,
        cell: (info) => {
          const row = info.row.original;
          const values = row.customFields && row.customFields[key];
          return values ? values : '-';
        },
      })
    } else if (fieldType === 'number') {
      return columnHelper.accessor(row => row.customFields?.[key], {
        id: key,
        header: () => customFieldTermin[key].name,
        size: 30,
        cell: (info) => {
          return info.getValue() ? customThousandSeparatorDot(info.getValue() as unknown as number) : '-';
        },
      })
    } else if (fieldType === 'time') {
      return columnHelper.accessor(row => row.customFields?.[key], {
        id: key,
        header: () => customFieldTermin[key].name,
        size: 30,
        cell: (info) => {
          return info.getValue() ? moment(info.getValue()).format("HH:mm") : '-';
        },
      })
    } else if (fieldType === 'pipeline') {
      return columnHelper.accessor(row => row.customFields?.[key], {
        id: key,
        header: () => customFieldTermin[key].name,
        size: 30,
        cell: (info) => {
          const row = info.row.original;
          const values = row.customFields && row.customFields[key];
          const findData = simplePipelines.find((item: SimplePipeline) => item.id === values);
          if (findData) {
            return (
              <div className="d-flex flex-column">
                <span><b>{findData.name}</b> / </span>
                <span>{findData.nominal} / </span>
                <span>{moment(findData.dateCreated).format("DD MMM YYYY")}</span>
              </div>
            );
          }
          return '-';
        },
      })
    } else if (fieldType === 'merchant') {
      return columnHelper.accessor(row => row.customFields?.[key], {
        id: key,
        header: () => customFieldTermin[key].name,
        size: 30,
        cell: (info) => {
          const row = info.row.original;
          const values = row.customFields && row.customFields[key];
          const findData = simpleMerchants.filter((item: SimpleMerchant) => item.id && values?.includes(item.id));
          if (findData.length > 0) {
            return findData.map((item: SimpleMerchant, index: number) => (
              <div className="d-flex flex-column">
                <span><b>{item.name}</b> / </span>
                <span>{item.phone} {index !== findData.length - 1 && ','}</span>
              </div>
            ));
          }
          return '-';
        },
      })
    } else if (fieldType === 'meeting') {
      return columnHelper.accessor(row => row.customFields?.[key], {
        id: key,
        header: () => customFieldTermin[key].name,
        size: 30,
        cell: (info) => {
          const row = info.row.original;
          const values = row.customFields && row.customFields[key];
          const findData = simpleMeetings.filter((item: SimpleMeeting) => item.id && values?.includes(item.id));
          if (findData.length > 0) {
            return findData.map((item: SimpleMeeting, index: number) => (
              <div className="d-flex flex-column">
                <span><b>{item.name}</b> / </span>
                <span>{moment(item.dateMeet).format('DD MMM YYYY')} {index !== findData.length - 1 && ','}</span>
              </div>
            ));
          }
          return '-';
        }
      })
    }
    return null;
  }).filter((column): column is NonNullable<typeof column> => column !== null);

  const handleDeleteItemTemp = (id: string) => {
    const terminToDelete = tempPaymentTermin.find(item => item.id === id);

    if (terminToDelete) {
      setUnpaymentNominal(prevState => fixFloatingPoint(prevState + terminToDelete.nominal, selectMultiPipeline));
      setTempPaymentTermin(prevState => prevState.filter(item => item.id !== id));
    }
  };

  const handleCheckIfTempValueIsExist = (row: PaymentTermin, info: CellContext<PaymentTermin, string>, type: "update" | "delete") => {
    if (tempPaymentTermin.length > 0) {
      Swal.fire({
        icon: 'warning',
        title: intl.formatMessage({ id: intl.formatMessage({ id: "ALERT.TITLE.WARNING" }) }),
        text: intl.formatMessage({ id: intl.formatMessage({ id: "FORM.LABEL.WARNING_TEMP_VALUE" }) }),
        confirmButtonText: 'Ok'
      })
      return
    } else {
      if (type === 'update') {
        handleClose();
        setSelectedTermin({ ...row, index: info.row.index });
        setShowUpdateModal(true);
        setSelectedTerminNumber(info.row.index + 1);
      } else if (type === 'delete') {
        handleClose();
        setShowDeleteModal(true);
        setSelectedTermin({ ...row, index: info.row.index });
        setSelectedTerminNumber(info.row.index + 1);
      }
    }
  }

  const columns = [
    columnHelper.accessor(row => row.id, {
      id: 'actions',
      header: () => intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.ACTION' }),
      size: 25,
      cell: (info) => {
        const row = info.row.original;
        if (row.progress === 'PENDING' && !row.isTemp) {
          return (
            <>
              <IconButton
                aria-label={`more-${row.id}`}
                onClick={(event) => {
                  setAnchorEl(event.currentTarget);
                  setOpenMenuId(row.id);
                }}
              >
                <MoreVertIcon />
              </IconButton>

              <Menu
                id={`menu-${row.id}`}
                open={openMenuId === row.id}
                anchorEl={anchorEl}
                onClose={handleClose}
              >
                <div>
                  <MenuItem onClick={() => {
                    handleCheckIfTempValueIsExist(row, info, 'update')
                  }}>
                    <i className="fas fa-edit fs-4 me-4"></i>
                    <span>{intl.formatMessage({ id: 'FORM.ACTION.UPDATE' })}</span>
                  </MenuItem>
                  <MenuItem onClick={() => {
                    handleClose();
                    setSelectedTermin({ ...row, index: info.row.index });
                    setShowPaymentModal(true);
                  }}>
                    <i className="fas fa-money-check-alt fs-3 me-5"></i>
                    <span>{intl.formatMessage({ id: 'FORM.ACTION.PAYMENT' })}</span>
                  </MenuItem>
                  <MenuItem onClick={() => {
                    handleCheckIfTempValueIsExist(row, info, 'delete')
                  }}>
                    <i className="fas fa-trash fs-3 me-5"></i>
                    <span>{intl.formatMessage({ id: 'FORM.ACTION.DELETE' })}</span>
                  </MenuItem>
                </div>
              </Menu>
            </>
          )
        } else if (row.isTemp) {
          return (
            <button
              className="btn btn-icon btn-bg-light btn-active-color-danger btn-sm"
              onClick={() => handleDeleteItemTemp(row.id)}
            >
              <i className="fas fa-trash"></i>
            </button>
          )
        }
        return <span>-</span>;
      },
    }),
    columnHelper.accessor(row => row.id, {
      id: 'termNo',
      header: () => intl.formatMessage({ id: 'FORM.LABEL.TABLE.TERM_TO' }),
      size: 25,
      cell: (info) => (
        <div className="d-flex align-items-center gap-2">
          <span>{info.row.index + 1}</span>
          {info.row.index === 0 && info.row.original.isDownPayment && (
            <span className="badge badge-primary">DP</span>
          )}
        </div>
      ),
    }),
    columnHelper.accessor('nominalBeforeVat', {
      header: () => `Nominal (${selectMultiPipeline.currencyCode})`,
      size: 30,
      cell: (info) => {
        const row = info.row.original;
        const isCompanyCurrency = selectMultiPipeline.currencyCode
        const isCompanyCurrencyNeedRound = selectMultiPipeline.currencyIsRounded
        if (row.isTemp) {
          return (
            <span className='d-flex justify-content-between align-items-center gap-3'>
              <span>{getCurrencySymbol(isCompanyCurrency)}</span>
              <span>{currencyFormatter((row.nominal).toString(), isCompanyCurrencyNeedRound, isCompanyCurrency)}</span>
            </span>)
        } else {
          return (
            <span className='d-flex justify-content-between align-items-center gap-3'>
              <span>{getCurrencySymbol(isCompanyCurrency)}</span>
              <span>{currencyFormatter((row.nominalBeforeVat || 0).toString(), isCompanyCurrencyNeedRound, isCompanyCurrency)}</span>
            </span>)
        }
      },
    }),
    columnHelper.accessor('vatPercentage', {
      header: () => intl.formatMessage({ id: 'FORM.LABEL.TABLE.VAT' }),
      size: 30,
      cell: (info) => {
        const _vat = info.getValue()
        const vat = _vat ? customNumberFormatString(_vat.toString()) : undefined

        return (
          <span className='d-flex justify-content-between align-items-center gap-3'>
            <span>
              {vat || "-"}
            </span>
            <span>{vat ? "%" : ""}</span>
          </span>)
      },
    }),
    columnHelper.accessor('vatNominal', {
      header: () => `Total (${selectMultiPipeline.currencyCode})`,
      size: 30,
      cell: (info) => {
        const row = info.row.original;
        const isCompanyCurrency = selectMultiPipeline.currencyCode
        const isCompanyCurrencyNeedRound = selectMultiPipeline.currencyIsRounded
        if (row.isTemp) {
          return (
            <span className='d-flex justify-content-between align-items-center gap-3'>
              <span>{getCurrencySymbol(isCompanyCurrency)}</span>
              <span>{currencyFormatter((row.total || 0).toString(), isCompanyCurrencyNeedRound, isCompanyCurrency)}</span>
            </span>)
        } else {
          return (
            <span className='d-flex justify-content-between align-items-center gap-3'>
              <span>{getCurrencySymbol(isCompanyCurrency)}</span>
              <span>{currencyFormatter((isCompanyCurrencyNeedRound ? Math.ceil(row.nominal) : row.nominal || 0).toString(), isCompanyCurrencyNeedRound, isCompanyCurrency)}</span>
            </span>)
        }
      },
    }),
    columnHelper.accessor('dateDue', {
      header: () => intl.formatMessage({ id: 'FORM.LABEL.DEADLINE_PAYMENT' }),
      size: 25,
      cell: (info) => {
        const getValue = info.getValue();
        if (getValue) {
          return moment(getValue).format("DD MMM YYYY")
        }
        return <span>-</span>
      },
    }),
    columnHelper.accessor('datePaid', {
      header: () => intl.formatMessage({ id: 'FORM.LABEL.PAYMENT_DATE' }),
      size: 25,
      cell: (info) => {
        const getValue = info.getValue();
        if (getValue) {
          return moment(getValue).format("DD MMM YYYY")
        }
        return <span>-</span>
      },
    }),
    columnHelper.accessor('receiptUrls', {
      header: () => intl.formatMessage({ id: 'FORM.LABEL.PROOF_OF_PAYMENT_TERM' }),
      size: 30,
      cell: (info) => {
        const row = info.row.original;
        if (info.getValue().length > 0) {
          return (
            <a
              href={`${info.getValue()[0]}`}
              className="btn btn-sm btn-outline btn-outline-default btn-active-light text-gray-600"
              target="_blank"
              rel="noopener noreferrer"
            >
              {intl.formatMessage({ id: 'FORM.LABEL.LOOK' })}
              <i className="fas fa-external-link-alt fs-6 ms-2 p-0"></i>
            </a>
          )
        } else if (row.isTemp) {
          const imageUrl = row.receiptUrls
          return imageUrl ? (
            <span role="button" onClick={() => {
              handleCheckIsTypeAttachment(imageUrl, 'receiptUrls')
            }}>
              {intl.formatMessage({ id: 'FORM.LABEL.LOOK' })}
              <i className="fas fa-external-link-alt fs-6 ms-2 p-0"></i>
            </span>
          ) : (
            <span>{intl.formatMessage({ id: 'FORM.LABEL.NO_PROOF' })}</span>
          );
        } else {
          return <span>{intl.formatMessage({ id: 'FORM.LABEL.NO_PROOF' })}</span>
        }
      },
    }),
    columnHelper.accessor('progress', {
      header: () => intl.formatMessage({ id: 'FORM.LABEL.STATUS_OF_PAYMENT' }),
      size: 25,
      cell: (info) => (
        info.getValue() === 'PENDING' ?
          <span className='badge bg-warning'>{info.getValue()}</span> :
          <span className='badge bg-success'>{info.getValue()}</span>
      ),
    }),
    ...columnCustomField,
  ]

  const table = useReactTable({
    data: mergeData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      columnPinning: {
        left: ['actions']
      }
    }
  })

  const formik = useFormik({
    initialValues: {
      togglePayment: false,
      inputType: 'nominal',
      nominal: '',
      vatPercentage: '',
      dateDue: '',
      datePaid: null as string | null,
      receiptUrls: null as File | null | string[] | string,
      ...Object.fromEntries(Object.keys(customFieldTermin).map((key) => [key, null])),
    },
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      try {
        const getFormatCurrency = currencyFormatter((unpaymentNominal).toString(), selectMultiPipeline.currencyIsRounded)
        if (unpaymentNominal > 0) {
          Swal.fire({
            icon: 'error',
            title: intl.formatMessage({ id: intl.formatMessage({ id: "FORM.LABEL.REMAINING_AMOUNT_UNPAYMENT" }) }),
            text: intl.formatMessage({ id: intl.formatMessage({ id: "FORM.LABEL.DYNAMIC_REMAINING_AMOUNT" }, { title: `${selectMultiPipeline.currencyCode === "IDR" ? "Rp" : "$"} ${getFormatCurrency}` }) }),
            confirmButtonText: 'Ok'
          })
          return
        }

        let successCount = 0;

        for (const termin of tempPaymentTermin) {
          try {
            const processedCustomFields: Record<string, any> = {};

            for (const [key, value] of Object.entries(termin.customFields || {})) {
              if (!value) {
                processedCustomFields[key] = null;
                continue;
              }

              const fieldType = customFieldTermin[key]?.type;

              if (fieldType === 'image' || fieldType === 'file' || fieldType === 'video') {
                if (value instanceof File) {
                  const response = await insertAttachment(value, 'PIPELINE', token);
                  processedCustomFields[key] = response.data.success ? response.data.data[0] : null;
                } else {
                  processedCustomFields[key] = value;
                }
              } else if (fieldType === 'images' || fieldType === 'files' || fieldType === 'videos') {
                if (Array.isArray(value) && value.some(item => item instanceof File)) {
                  const files = value.filter(item => item instanceof File);
                  if (files.length > 0) {
                    const response = await insertMultiAttachment(files, 'PIPELINE', token);
                    processedCustomFields[key] = response.data.success ? response.data.data : null;
                  } else {
                    processedCustomFields[key] = value;
                  }
                } else {
                  processedCustomFields[key] = value;
                }
              } else if (fieldType === 'dateTimeRange') {
                if (Array.isArray(value)) {
                  processedCustomFields[key] = value.map(date =>
                    date ? new Date(date).toISOString() : null
                  );
                } else {
                  processedCustomFields[key] = null;
                }
              } else {
                processedCustomFields[key] = value;
              }
            }

            let processedReceiptUrls = termin.receiptUrls;
            if (termin.receiptUrls instanceof File) {
              const response = await insertAttachment(termin.receiptUrls, 'PIPELINE', token);
              processedReceiptUrls = response.data.success ? [response.data.data[0]] : [];
            }

            const newTermin: ICreatePaymentTermin = {
              leadId: pipeline.id,
              nominal: termin.nominal,
              vatPercentage: termin.vatPercentage,
              dateDue: termin.dateDue,
              datePaid: termin.datePaid,
              customFields: processedCustomFields,
              progress: termin.progress,
              receiptUrls: processedReceiptUrls,
            };

            const response = await createPaymentTermin(newTermin, token);
            if (response.success) {
              successCount++;
            }
          } catch (error) {
            console.error('Error processing termin:', error);
          }
        }

        if (successCount === tempPaymentTermin.length) {
          await mutateTermin();
          Swal.fire(
            intl.formatMessage({ id: 'FORM.CONFIRM_SUCCESS' }),
            intl.formatMessage({ id: 'FORM.CONFIRM.TERMIN_CREATED' }),
            'success'
          );
          resetForm();
          setTempPaymentTermin([]);
        } else {
          Swal.fire({
            icon: "error",
            text: `${intl.formatMessage({ id: "FORM.VALIDATION.ADD_FAILED" })}.`,
            heightAuto: false,
            confirmButtonText: 'Ok'
          });
        }
      } catch (error: any) {
        console.error('Error creating payment termin:', error);
        Swal.fire({
          icon: "error",
          text: `${intl.formatMessage({ id: "FORM.VALIDATION.ADD_FAILED" })}.`,
          heightAuto: false,
          confirmButtonText: 'Ok'
        });
      }
    },
  });

  const validateTerminForm = (values: any, customFieldTermin?: CustomField, formik?: any,) => {
    const errors: any = {};

    if (values.nominal > unpaymentNominal) {
      errors.nominal = intl.formatMessage({ id: "FORM.LABEL.REMAINING_UNPAYMENT_ERROR" })
    }

    if (values.nominal === '') {
      errors.nominal = intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.PAYMENT_AMOUNT" }) });
    }

    if (values.dateDue === '') {
      errors.dateDue = intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.DEADLINE_PAYMENT" }) });
    }

    if (formik?.values.togglePayment && values.datePaid === '') {
      errors.datePaid = intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.PAYMENT_DATE" }) });
    }

    if (customFieldTermin) {
      const customFieldErrors: any = {};
      Object.entries(customFieldTermin).forEach(([key, field]) => {
        const value = values.customFields?.[key];

        if (field.required) {
          if (['image', 'images', 'file', 'files', 'video', 'videos'].includes(field.type)) {
            if (!value || (Array.isArray(value) && value.length === 0)) {
              customFieldErrors[key] = intl.formatMessage(
                { id: "FORM.VALIDATION.FIELD_REQUIRED" },
                { title: field.name || 'Custom Field' }
              );
            }
          }
          else if (!values.customFields || !value) {
            customFieldErrors[key] = intl.formatMessage(
              { id: "FORM.VALIDATION.FIELD_REQUIRED" },
              { title: field.name || 'Custom Field' }
            );
          }
        }

        if (field.type === 'link' && value) {
          try {
            new URL(value);
          } catch (e) {
            customFieldErrors[key] = intl.formatMessage({ id: 'FORM.VALIDATION.MUST_LINK' });
          }
        }
      });
      if (Object.keys(customFieldErrors).length > 0) {
        errors.customFields = customFieldErrors;
      }
    }

    return errors
  }

  const handleAddPaymentTermin = () => {
    let currentPaymentAmount: number = 0;
    if (formik.values.inputType === 'nominal') {
      currentPaymentAmount = Number(formik.values.nominal.replaceAll(",", ""));
    } else {
      const percentageValue = parseFloat(formik.values.nominal);
      currentPaymentAmount = user?.data.companyCurrency === 'IDR' ? Math.round((percentageValue / 100) * pipeline.nominal) : (percentageValue / 100) * pipeline.nominal;
    }

    let vatPercentage = Number(formik.values.vatPercentage.replaceAll(".", "")) ? Number(formik.values.vatPercentage) : 0
    let nominalWithVAT = vatPercentage ? Math.round(currentPaymentAmount + currentPaymentAmount * vatPercentage / 100) : currentPaymentAmount

    // Membuat objek customFields dari nilai-nilai formik
    const customFieldsObj = Object.keys(customFieldTermin).reduce((obj, key) => {
      obj[key] = formik.values[key as keyof typeof formik.values];
      return obj;
    }, {} as Record<string, any>);

    const terminFormValues: PaymentTermin = {
      id: uuidv4(),
      leadId: pipeline.id,
      companyId: user.data.company_id,
      nominal: currentPaymentAmount,
      vatPercentage: Number(formik.values.vatPercentage.replaceAll(".", "")),
      dateDue: formik.values.togglePayment ? (formik.values.datePaid || '') : formik.values.dateDue,
      datePaid: formik.values.togglePayment ? (formik.values.datePaid || '') : null,
      progress: formik.values.togglePayment ? 'PAID' : 'PENDING',
      dateCreated: moment().format('YYYY-MM-DD'),
      receiptUrls: (formik.values.receiptUrls as string[]) || [],
      customFields: customFieldsObj,
      total: nominalWithVAT,
      isTemp: true,
    }

    const errors = validateTerminForm(terminFormValues, customFieldTermin, formik)
    setTerminErrors(errors)

    if (Object.keys(errors).length > 0) {
      return
    }

    const checkDateDownPayment = data.find((item: PaymentTermin) => item.isDownPayment && item.dateDue && item.dateDue > formik.values.dateDue)
    if (checkDateDownPayment) {
      Swal.fire({
        icon: 'error',
        title: intl.formatMessage({ id: 'FORM.VALIDATION.TERMIN_DATE_DOWN_PAYMENT_ERROR_TITLE' }),
        text: intl.formatMessage({ id: 'FORM.VALIDATION.TERMIN_DATE_DOWN_PAYMENT_ERROR' }),
      })
      return
    }

    setUnpaymentNominal(prevState => fixFloatingPoint(prevState - currentPaymentAmount, selectMultiPipeline));

    setTempPaymentTermin(prevState => [...prevState, terminFormValues]);

    formik.resetForm();
    inputRef.current.forEach(ref => {
      if (ref) {
        ref.value = '';
      }
    });
  }

  // Replace handleNominalPembayaranChange
  const handleNominalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/[^\d]/g, '');
    const value = e.target.value ?? '0';
    const currencyFormatterValidation = currencyFormatterPreventPropagation(value);

    if (currencyFormatterValidation.hasTrailingMultipleDot) return
    if (formik.values.inputType === 'percentage') {
      if (Number(value) > 100) {
        setTerminErrors((prevState: any) => ({ ...prevState, nominal: intl.formatMessage({ id: 'FORM.LABEL.PERCENTAGE_MAX' }) }));
        return;
      } else {
        setTerminErrors((prevState: any) => ({ ...prevState, nominal: '' }));
      }
      formik.setFieldValue('nominal', value);
    } else {
      const isCompanyCurrencyIdr = selectMultiPipeline.currencyCode === 'IDR'
      if (isCompanyCurrencyIdr) {
        if (value === '0') {
          setTerminErrors((prevState: any) => ({ ...prevState, nominal: intl.formatMessage({ id: 'FORM.LABEL.PAYMENT_AMOUNT_0' }) }));
          return;
        } else {
          setTerminErrors((prevState: any) => ({ ...prevState, nominal: '' }));
        }
      }
      if (currencyFormatterValidation.hasTrailingDot || currencyFormatterValidation.hasTrailingDotZero) {
        return formik.setFieldValue('nominal', value);
      }
      const formattedValue = currencyFormatter(value, selectMultiPipeline.currencyIsRounded);
      formik.setFieldValue('nominal', formattedValue);
    }
  };

  const handleVATTerminChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/[^\d]/g, '');


    const formattedValue = rawValue.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    if (Number(rawValue) > 100) {
      setTerminErrors((prevState: any) => ({ ...prevState, vatPercentage: intl.formatMessage({ id: 'FORM.LABEL.PERCENTAGE_MAX' }) }));
      return;
    }
    formik.setFieldValue('vatPercentage', formattedValue);

  };

  // Replace handlePaymentDate
  const handleDateChange = (date: Date | null) => {
    if (date === null) {
      if (formik.values.togglePayment) {
        formik.setFieldValue('datePaid', '');
      } else {
        formik.setFieldValue('dateDue', '');
      }
    } else {
      if (formik.values.togglePayment) {
        formik.setFieldValue('datePaid', moment(date).format('YYYY-MM-DD'));
      } else {
        const filterDate = moment(date).format('YYYY-MM-DD');
        formik.setFieldValue('dateDue', filterDate);
      }
    }
  };

  useEffect(() => {
    const sortTableByDateDue = (data: any) => {
      return data.sort((a: any, b: any) => new Date(a.dateDue).getTime() - new Date(b.dateDue).getTime());
    }
    setMergeData(sortTableByDateDue([...data, ...tempPaymentTermin]))
  }, [tempPaymentTermin, data])

  useEffect(() => { // reset tanggal ketika terjadi perubahan togglePayment
    if (!formik.values.togglePayment) {
      formik.setFieldValue('dateDue', '')
      formik.setFieldValue('datePaid', '')
    }
  }, [formik.values.togglePayment])

  useEffect(() => {
    formik.setFieldValue('nominal', '')
  }, [formik.values.inputType])

  useModalEffect({
    showModal: showDeleteModal,
    setShowModal: setShowDeleteModal,
    modalId: `delete-detail-termin-modal-${pipeline.id}`
  });

  useModalEffect({
    showModal: showUpdateModal,
    setShowModal: setShowUpdateModal,
    modalId: `update-detail-termin-modal-${pipeline.id}`
  });

  useModalEffect({
    showModal: showPaymentModal,
    setShowModal: setShowPaymentModal,
    modalId: `payment-detail-termin-modal-${pipeline.id}`
  });

  useModalEffect({
    showModal: showAttachmentModal,
    setShowModal: setShowAttachmentModal,
    modalId: `attachments-detail-termin-modal-${pipeline.id}`
  });

  const { handleCloseModal } = useHandleCloseModalTermin({ tempPaymentTermin, formik, closeModalRef, modalRef });

  return (
    <>
      {
        isModal ?
          <ModalContentTermin
            pipeline={pipeline}
            modalRef={modalRef}
            isLoading={isLoading}
            intl={intl}
            handleCloseModal={handleCloseModal}
            switchPaidSchedule={switchPaidSchedule}
            setSwitchPaidSchedule={setSwitchPaidSchedule}
            table={table}
            errorTermin={errorTermin}
            unpaymentNominal={unpaymentNominal}
            selectMultiPipeline={selectMultiPipeline}
            formik={formik}
            terminErrors={terminErrors}
            customFieldTermin={customFieldTermin}
            handleNominalChange={handleNominalChange}
            handleVATTerminChange={handleVATTerminChange}
            handleDateChange={handleDateChange}
            isAfterToday={isAfterToday}
            isBeforeToday={isBeforeToday}
            handleAddPaymentTermin={handleAddPaymentTermin}
            closeModalRef={closeModalRef}
            showUpdateModal={showUpdateModal}
            selectedTermin={selectedTermin as PaymentTermin & { index: number }}
            selectedTerminNumber={selectedTerminNumber}
            mutateTermin={mutateTermin}
            showAttachmentModal={showAttachmentModal}
            terminUrl={terminUrl}
            isTypeFile={isTypeFile}
            terminFiles={terminFiles}
            handleClosePreview={handleClosePreview}
            showPaymentModal={showPaymentModal}
            data={data}
            tempPaymentTermin={tempPaymentTermin}
            setShowUpdateModal={setShowUpdateModal}
            setSelectedTermin={setSelectedTermin}
            setShowDeleteModal={setShowDeleteModal}
            setUnpaymentNominal={setUnpaymentNominal}
            terminKey={terminKey}
            setShowAttachmentModal={setShowAttachmentModal}
            showDeleteModal={showDeleteModal}
            setShowPaymentModal={setShowPaymentModal}
          />
          :
          <CardContentTermin
            pipeline={pipeline}
            modalRef={modalRef}
            isLoading={isLoading}
            intl={intl}
            handleCloseModal={handleCloseModal}
            switchPaidSchedule={switchPaidSchedule}
            setSwitchPaidSchedule={setSwitchPaidSchedule}
            table={table}
            errorTermin={errorTermin}
            unpaymentNominal={unpaymentNominal}
            selectMultiPipeline={selectMultiPipeline}
            formik={formik}
            terminErrors={terminErrors}
            customFieldTermin={customFieldTermin}
            handleNominalChange={handleNominalChange}
            handleVATTerminChange={handleVATTerminChange}
            handleDateChange={handleDateChange}
            isAfterToday={isAfterToday}
            isBeforeToday={isBeforeToday}
            handleAddPaymentTermin={handleAddPaymentTermin}
            closeModalRef={closeModalRef}
            showUpdateModal={showUpdateModal}
            selectedTermin={selectedTermin as PaymentTermin & { index: number }}
            selectedTerminNumber={selectedTerminNumber}
            mutateTermin={mutateTermin}
            showAttachmentModal={showAttachmentModal}
            terminUrl={terminUrl}
            isTypeFile={isTypeFile}
            terminFiles={terminFiles}
            handleClosePreview={handleClosePreview}
            showPaymentModal={showPaymentModal}
            data={data}
            tempPaymentTermin={tempPaymentTermin}
            setShowUpdateModal={setShowUpdateModal}
            setSelectedTermin={setSelectedTermin}
            setShowDeleteModal={setShowDeleteModal}
            setUnpaymentNominal={setUnpaymentNominal}
            terminKey={terminKey}
            setShowAttachmentModal={setShowAttachmentModal}
            showDeleteModal={showDeleteModal}
            setShowPaymentModal={setShowPaymentModal}
          />
      }
    </>

  )
}

export default DetailTerminPipeline